import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { MainPageBlock } from "@bongeunsa/db";
import type { Language } from "@bongeunsa/i18n";
import { resolveText } from "@/lib/i18nResolve.js";
import { ComingSoonModal } from "@bongeunsa/ui";
import { Portal } from "@/components/Portal.js";
import { api } from "@/lib/api.js";
import { uiStrings } from "@bongeunsa/i18n";

type CtaAction = { key: string; url: string };

interface CardBlockProps {
  block: MainPageBlock;
  lang: Language;
  defaultLang: Language;
  mainPageId: string;
}

export function CardBlock({ block, lang, defaultLang, mainPageId }: CardBlockProps) {
  const i18nParsed = (() => {
    try { return JSON.parse(block.i18n) as Record<Language, Record<string, string>>; } catch { return { ko: {}, en: {}, zh: {}, ja: {} } as Record<Language, Record<string, string>>; }
  })();
  const config = (() => {
    try { return JSON.parse(block.config) as Record<string, unknown>; } catch { return {}; }
  })();

  const text = resolveText(i18nParsed, lang, defaultLang);

  const photoUrl = config.photo_url as string | undefined;
  const hasPhoto = config.has_photo === "true" && !!photoUrl;
  const hasText = config.has_text !== "false" && !!(text.title || text.body || text.annotation);
  const hasCta = config.has_cta === "true";
  const ctaType = (config.cta_type as string) || "single";
  const ctaUrl = config.cta_url as string | undefined;
  const ctaComingSoon = config.cta_coming_soon === "true";
  const actions: CtaAction[] = (() => {
    try { return JSON.parse(config.cta_actions as string ?? "[]") as CtaAction[]; } catch { return []; }
  })();

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  async function trackClick() {
    await api.post("/api/main-pages/track-click", { main_page_id: mainPageId, block_id: block.id, lang }).catch(() => {});
  }

  if (!hasPhoto && !hasText && !hasCta) return null;

  return (
    <div className="overflow-hidden rounded-2xl glass-card-sm">
      {hasPhoto && (
        <img src={photoUrl} alt={text.alt ?? ""} className="w-full object-cover" loading="lazy" />
      )}

      {hasText && (
        <div className="px-5 py-4">
          {text.title && (
            <h2 className="mb-1 text-lg font-bold text-gray-900">{text.title}</h2>
          )}
          {text.body && (
            <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600">{text.body}</p>
          )}
          {text.annotation && (
            <p className="mt-1.5 text-xs text-gray-400">{text.annotation}</p>
          )}
        </div>
      )}

      {hasCta && (
        <div className="px-5 pb-5">
          {ctaType === "single" && (
            <CtaSingle url={ctaUrl} label={text.cta_label || uiStrings[lang].common.link} comingSoon={ctaComingSoon}
              lang={lang} onComingSoon={() => setComingSoonOpen(true)} onTrack={trackClick} />
          )}
          {ctaType === "group" && (
            <>
              <button type="button" onClick={() => setGroupModalOpen(true)}
                className="flex w-full items-center justify-between rounded-xl bg-gray-100 px-5 py-3.5 text-sm font-semibold text-gray-700 hover:bg-gray-200">
                <span>{text.cta_label || uiStrings[lang].common.link}</span>
                <span>···</span>
              </button>
              <ActionGroupModal
                open={groupModalOpen}
                actions={actions}
                text={text}
                lang={lang}
                onClose={() => setGroupModalOpen(false)}
                onTrack={trackClick}
              />
            </>
          )}
          {ctaType === "wide_dot" && (
            <div className="flex gap-2">
              <CtaSingle url={ctaUrl} label={text.cta_label || uiStrings[lang].common.link} comingSoon={ctaComingSoon}
                lang={lang} onComingSoon={() => setComingSoonOpen(true)} onTrack={trackClick} className="flex-1" />
              {actions.length > 0 && (
                <>
                  <button type="button" onClick={() => setGroupModalOpen(true)}
                    className="aspect-square rounded-xl bg-gray-100 text-sm font-semibold text-gray-700 hover:bg-gray-200 flex items-center justify-center"
                    style={{ minWidth: "3.5rem" }}>
                    ···
                  </button>
                  <ActionGroupModal
                    open={groupModalOpen}
                    actions={actions}
                    text={text}
                    lang={lang}
                    onClose={() => setGroupModalOpen(false)}
                    onTrack={trackClick}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}

      <ComingSoonModal open={comingSoonOpen} onClose={() => setComingSoonOpen(false)} lang={lang} />
    </div>
  );
}

function CtaSingle({
  url,
  label,
  comingSoon,
  lang,
  onComingSoon,
  onTrack,
  className = "",
}: {
  url?: string;
  label: string;
  comingSoon: boolean;
  lang: Language;
  onComingSoon: () => void;
  onTrack: () => void;
  className?: string;
}) {
  if (comingSoon || !url) {
    return (
      <button type="button" onClick={onComingSoon}
        className={`flex items-center justify-between rounded-xl bg-gray-100 px-5 py-3.5 text-sm font-semibold text-gray-500 hover:bg-gray-200 ${className}`}>
        <span>{label}</span>
        <span className="text-xs text-gray-400">{uiStrings[lang].common.preparing}</span>
      </button>
    );
  }
  return (
    <a href={url} target={url.startsWith("http") ? "_blank" : undefined}
      rel={url.startsWith("http") ? "noopener noreferrer" : undefined}
      onClick={onTrack}
      className={`flex items-center justify-between rounded-xl bg-brand-500 px-5 py-3.5 text-sm font-semibold text-white hover:bg-brand-600 active:bg-brand-700 ${className}`}>
      <span>{label}</span>
      <span>→</span>
    </a>
  );
}

function ActionGroupModal({
  open,
  actions,
  text,
  lang,
  onClose,
  onTrack,
}: {
  open: boolean;
  actions: CtaAction[];
  text: Record<string, string>;
  lang: Language;
  onClose: () => void;
  onTrack: () => void;
}) {
  const s = uiStrings[lang];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <Portal>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
              onClick={onClose}
            />
            <motion.div
              key="modal"
              initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
              animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
              exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className="fixed left-1/2 top-1/2 z-50 w-[360px] max-w-[90vw] rounded-2xl bg-white p-5 shadow-2xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">{s.card.selectOption}</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-2">
                {actions.map((action) => {
                  const label = (text[action.key] ?? "").trim() || action.key;
                  if (!action.url) return null;
                  return (
                    <a key={action.key} href={action.url}
                      target={action.url.startsWith("http") ? "_blank" : undefined}
                      rel={action.url.startsWith("http") ? "noopener noreferrer" : undefined}
                      onClick={() => { void onTrack(); onClose(); }}
                      className="flex items-center justify-between rounded-xl bg-gray-50 px-5 py-3.5 text-sm font-medium text-gray-700 hover:bg-gray-100">
                      <span>{label}</span>
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
}
